import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Group,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconChevronRight,
  IconChevronLeft,
  IconMaximize,
} from '@tabler/icons-react';

interface ImageOption {
  name: string;
  link: string;
}

interface ImagePickerProps {
  label?: string;
  availableImages: ImageOption[];
  maxSelection: number;
  value: string[];
  onChange: (selectedImages: string[]) => void;
  isDisabled?: boolean;
}

export const ImagePicker = ({
  label,
  availableImages,
  maxSelection,
  value = [],
  onChange,
  isDisabled = false,
}: ImagePickerProps) => {
  const [tempSelection, setTempSelection] = useState<string[]>(value);
  const [isOpen, setIsOpen] = useState(false);
  const [isSlideshow, setIsSlideshow] = useState(false);
  const [slideshowIndex, setSlideshowIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(24);
  const galleryRef = useRef<HTMLDivElement>(null);
  const intl = useIntl();

  const imagesPerLoad = 24;
  const displayedImages = availableImages.slice(0, visibleCount);
  const hasMore = visibleCount < availableImages.length;

  const handleImageClick = (imageName: string) => {
    if (tempSelection.includes(imageName)) {
      setTempSelection(tempSelection.filter((name) => name !== imageName));
    } else if (tempSelection.length < maxSelection) {
      setTempSelection([...tempSelection, imageName]);
    }
  };

  const handleOpenSlideshow = (index: number) => {
    setSlideshowIndex(index);
    setIsSlideshow(true);
  };

  const handleCloseSlideshow = () => {
    setIsSlideshow(false);
  };

  const handleNextSlide = () => {
    setSlideshowIndex((prev) => (prev + 1) % availableImages.length);
  };

  const handlePrevSlide = () => {
    setSlideshowIndex((prev) =>
      prev === 0 ? availableImages.length - 1 : prev - 1
    );
  };

  const handleConfirm = () => {
    onChange(tempSelection);
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempSelection(value);
    setIsOpen(false);
  };

  const handleOpen = () => {
    setTempSelection(value);
    setVisibleCount(24);
    setIsOpen(true);
  };

  // Keyboard navigation for slideshow
  useEffect(() => {
    if (!isSlideshow) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          handlePrevSlide();
          break;
        case 'ArrowRight':
          handleNextSlide();
          break;
        case 'Escape':
          handleCloseSlideshow();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSlideshow, slideshowIndex]);

  // Disable body scroll when slideshow is open
  useEffect(() => {
    if (isSlideshow) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isSlideshow]);

  // Infinite scroll - load more images when scrolling near bottom
  useEffect(() => {
    const galleryElement = galleryRef.current;
    if (!galleryElement || !isOpen) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = galleryElement;
      const scrolledToBottom = scrollHeight - scrollTop - clientHeight < 200;

      if (scrolledToBottom && hasMore) {
        setVisibleCount((prev) => prev + imagesPerLoad);
      }
    };

    galleryElement.addEventListener('scroll', handleScroll);
    return () => galleryElement.removeEventListener('scroll', handleScroll);
  }, [isOpen, hasMore, imagesPerLoad]);

  // Auto-load more images if content doesn't fill the container (no scrollbar)
  useEffect(() => {
    const galleryElement = galleryRef.current;
    if (!galleryElement || !isOpen || !hasMore) return;

    const checkAndLoadMore = () => {
      const { scrollHeight, clientHeight } = galleryElement;
      // If content height is less than or equal to container height, load more
      if (scrollHeight <= clientHeight) {
        setVisibleCount((prev) => prev + imagesPerLoad);
      }
    };

    // Check after a short delay to ensure images are rendered
    const timeoutId = setTimeout(checkAndLoadMore, 100);

    return () => clearTimeout(timeoutId);
  }, [isOpen, hasMore, visibleCount, imagesPerLoad]);

  return (
    <Stack gap="xs" w="100%">
      {label && (
        <Text size="sm" fw={500}>
          {label}
        </Text>
      )}

      <Button
        size="xs"
        variant="light"
        onClick={handleOpen}
        disabled={isDisabled}
        fullWidth
      >
        {intl.formatMessage({ id: 'selectImages' })} ({value.length}/
        {maxSelection})
      </Button>

      {isOpen &&
        createPortal(
          <Box
            bg="dark.9"
            h="100svh"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10000,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              p="md"
              style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <Flex justify="space-between" align="center" mb="xs">
                <Text size="lg" fw={700} c="white">
                  {intl.formatMessage({ id: 'selectImages' })}
                </Text>
                <ActionIcon
                  onClick={handleCancel}
                  variant="subtle"
                  c="white"
                  size="lg"
                >
                  <IconX size={20} />
                </ActionIcon>
              </Flex>
              <Text size="sm" c="dimmed">
                {intl.formatMessage({ id: 'selected' })}: {tempSelection.length}{' '}
                / {maxSelection}
              </Text>
            </Box>

            {/* Gallery */}
            <Box ref={galleryRef} style={{ flex: 1, overflow: 'auto' }} p="md">
              <Stack gap="md">
                <SimpleGrid cols={{ base: 3, sm: 4, md: 6, lg: 8 }} spacing={4}>
                  {displayedImages.map((image, index) => {
                    const isSelected = tempSelection.includes(image.name);
                    const canSelect =
                      tempSelection.length < maxSelection || isSelected;

                    return (
                      <Box
                        key={image.name}
                        onClick={() =>
                          canSelect && handleImageClick(image.name)
                        }
                        style={{
                          position: 'relative',
                          cursor: canSelect ? 'pointer' : 'not-allowed',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          opacity: canSelect ? 1 : 0.4,
                          aspectRatio: '1',
                          border: isSelected
                            ? '3px solid var(--mantine-color-blue-5)'
                            : '1px solid rgba(255, 255, 255, 0.1)',
                          transition: 'all 0.2s ease',
                          transform: 'scale(1)',
                        }}
                        onMouseEnter={(e) => {
                          if (canSelect) {
                            e.currentTarget.style.transform = 'scale(0.98)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        <img
                          src={image.link}
                          alt={image.name}
                          decoding="async"
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />

                        {/* Image name overlay */}
                        <Box
                          style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            background:
                              'linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent)',
                            padding: '24px 8px 8px 8px',
                            pointerEvents: 'none',
                          }}
                        >
                          <Text
                            size="xs"
                            c="white"
                            style={{
                              textAlign: 'center',
                              lineHeight: '1.2',
                              wordBreak: 'break-word',
                            }}
                          >
                            {image.name}
                          </Text>
                        </Box>

                        {/* Fullscreen button */}
                        <ActionIcon
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            backdropFilter: 'blur(4px)',
                          }}
                          variant="filled"
                          size="sm"
                          color="dark"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenSlideshow(index);
                          }}
                        >
                          <IconMaximize size={16} />
                        </ActionIcon>

                        {/* Selection indicator */}
                        {isSelected && (
                          <Box
                            style={{
                              position: 'absolute',
                              top: '4px',
                              left: '4px',
                              backgroundColor: 'var(--mantine-color-blue-5)',
                              borderRadius: '50%',
                              width: '22px',
                              height: '22px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                            }}
                          >
                            <IconCheck size={14} color="white" stroke={3} />
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </SimpleGrid>
              </Stack>
            </Box>

            {/* Footer */}
            <Box
              p="md"
              style={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}
            >
              <Group justify="center" gap="md">
                <Button variant="default" onClick={handleCancel}>
                  {intl.formatMessage({ id: 'cancel' })}
                </Button>
                <Button onClick={handleConfirm}>
                  {intl.formatMessage({ id: 'confirmSelection' })}
                </Button>
              </Group>
            </Box>

            {/* Slideshow Overlay — avoid loading="lazy" here: iOS Safari often skips load inside position:fixed */}
            {isSlideshow && (
              <Box
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.98)',
                  zIndex: 20000,
                  display: 'flex',
                  flexDirection: 'column',
                  paddingTop: 'env(safe-area-inset-top, 0px)',
                  paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                }}
              >
                {/* Close button */}
                <ActionIcon
                  onClick={handleCloseSlideshow}
                  style={{ position: 'absolute', top: '16px', right: '16px' }}
                  variant="subtle"
                  c="white"
                  size="lg"
                >
                  <IconX size={24} />
                </ActionIcon>

                {/* Previous button */}
                <ActionIcon
                  onClick={handlePrevSlide}
                  style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2001,
                  }}
                  variant="subtle"
                  c="white"
                  size="lg"
                >
                  <IconChevronLeft size={28} />
                </ActionIcon>

                {/* Title + counter (fixed height strip) */}
                <Stack
                  align="center"
                  gap="xs"
                  px="md"
                  pt={48}
                  pb="sm"
                  style={{ flexShrink: 0, maxWidth: '100%' }}
                >
                  <Text
                    size="lg"
                    fw={500}
                    c="white"
                    style={{
                      textAlign: 'center',
                      lineHeight: '1.2',
                      textShadow:
                        '0 2px 8px rgba(0, 0, 0, 0.8), 0 0 4px rgba(0, 0, 0, 0.9)',
                      wordBreak: 'break-word',
                    }}
                  >
                    {availableImages[slideshowIndex].name}
                  </Text>

                  <Text
                    size="sm"
                    c="dimmed"
                    style={{
                      lineHeight: '1.2',
                      textShadow: '0 1px 4px rgba(0, 0, 0, 0.8)',
                    }}
                  >
                    {slideshowIndex + 1} / {availableImages.length}
                  </Text>
                </Stack>

                {/* Image: flex middle gives WebKit a real box so img can size with object-fit */}
                <Box
                  style={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 56px 88px',
                    boxSizing: 'border-box',
                  }}
                >
                  <img
                    key={`slideshow-${slideshowIndex}-${availableImages[slideshowIndex].name}`}
                    src={availableImages[slideshowIndex].link}
                    alt={availableImages[slideshowIndex].name}
                    decoding="async"
                    loading="eager"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      borderRadius: '8px',
                    }}
                  />
                </Box>

                {/* Next button */}
                <ActionIcon
                  onClick={handleNextSlide}
                  style={{
                    position: 'absolute',
                    right: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 2001,
                  }}
                  variant="subtle"
                  c="white"
                  size="lg"
                >
                  <IconChevronRight size={28} />
                </ActionIcon>

                {/* Selection button */}
                <Button
                  style={{
                    position: 'absolute',
                    bottom: '32px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                  variant={
                    tempSelection.includes(availableImages[slideshowIndex].name)
                      ? 'filled'
                      : 'default'
                  }
                  onClick={() =>
                    handleImageClick(availableImages[slideshowIndex].name)
                  }
                  disabled={
                    !tempSelection.includes(
                      availableImages[slideshowIndex].name
                    ) && tempSelection.length >= maxSelection
                  }
                  leftSection={
                    tempSelection.includes(
                      availableImages[slideshowIndex].name
                    ) ? (
                      <IconCheck size={16} />
                    ) : null
                  }
                >
                  {tempSelection.includes(availableImages[slideshowIndex].name)
                    ? intl.formatMessage({ id: 'selected' })
                    : intl.formatMessage({ id: 'select' })}
                </Button>
              </Box>
            )}
          </Box>,
          document.body
        )}

      {value.length > 0 && (
        <Flex wrap="wrap" gap="xs" mt="xs">
          {value.map((imageName) => {
            const image = availableImages.find((img) => img.name === imageName);
            if (!image) return null;

            return (
              <Box
                key={imageName}
                style={{
                  position: 'relative',
                  borderRadius: '4px',
                  overflow: 'hidden',
                  border: '1px solid var(--mantine-color-gray-3)',
                }}
              >
                <img
                  src={image.link}
                  alt={image.name}
                  width={100}
                  height={100}
                  decoding="async"
                  loading="lazy"
                  style={{
                    objectFit: 'cover',
                  }}
                />
                <Box
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    fontSize: '10px',
                    padding: '4px',
                    textAlign: 'center',
                  }}
                >
                  {image.name}
                </Box>
              </Box>
            );
          })}
        </Flex>
      )}
    </Stack>
  );
};
